<script setup>
import { endOfMonth, startOfMonth } from '@/@core/utils/formatters'
import { cookieRef } from '@/@layouts/stores/config'
import TicketTable from "@/views/feedback/ticket-management/TicketTable.vue"
import { computed } from 'vue'
import CapaManagement from "@/views/feedback/dashboard/ticket/tabs/CapaManagement.vue"
import CategoryDeepDive from "@/views/feedback/dashboard/ticket/tabs/CategoryDeepDive.vue"
import ExecutiveSummary from "@/views/feedback/dashboard/ticket/tabs/ExecutiveSummary.vue"
import LocationAnalytics from "@/views/feedback/dashboard/ticket/tabs/LocationAnalytics.vue"
import OperationsStaff from "@/views/feedback/dashboard/ticket/tabs/OperationsStaff.vue"
import RcaCaAnalysis from "@/views/feedback/dashboard/ticket/tabs/RcaCaAnalysis.vue"
import TatSlaPerformance from "@/views/feedback/dashboard/ticket/tabs/TatSlaPerformance.vue"

definePage({
  meta: {
    name: 'dashboard',
    subject: 'feedback.menu',
    action: 'dashboard_ticket',
  },  
})


const unit_id = ref(cookieRef('selectedUnit') ?? null)

const addDays = (date, days) => {
  const result = new Date(date)

  result.setDate(result.getDate() + days)
  
  return result
}


const addMonths = (date, months) => {
  const result = new Date(date)

  result.setMonth(result.getMonth() + months)
  
  return result
}

const customDateFormat = date => {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
}

const quickDateRangeOptions =[{
  title: 'Today',
  value: `${customDateFormat(new Date())}`,
},
{
  title: 'Yesterday',
  value: `${customDateFormat(addDays(new Date(), -1))}`,
},
{
  title: 'Last 7 days',
  value: `${customDateFormat(addDays(new Date(), -6))} to ${customDateFormat(new Date())}`,
},
{
  title: 'Last 30 days',
  value: `${customDateFormat(addDays(new Date(), -30))} to ${customDateFormat(new Date())}`,
},
{
  title: 'This month',
  value: `${customDateFormat(startOfMonth(new Date()))} to ${customDateFormat(new Date())}`,
},
{
  title: 'Last month',
  value: `${customDateFormat(startOfMonth(addMonths(new Date(), -1)))} to ${customDateFormat(endOfMonth(addMonths(new Date(), -1)))}`,
},
{
  title: 'Custom',
  value: null,
}]


const quickDateRange = ref('Last 30 days')

const dateRange = ref()

const fromDate = ref(customDateFormat(addDays(new Date(), -30)))
const toDate = ref(customDateFormat(new Date()))

const dateRangeOptions = ref({
  mode: 'range',
  maxDate: new Date(),
  dateFormat: 'Y-m-d',
  enableTime: false,
  enableSeconds: false,
  noCalendar: false,
  altInput: true,
  altFormat: 'F j, Y',
  altInputClass: 'form-control',
  altInputPlaceholder: 'Select date',
  inline: false,
  static: false,
  appendTo: document.body,
  clickOpens: true,
  defaultDate: [new Date(), new Date()],
  defaultDate: [customDateFormat(addDays(new Date(), -30)), customDateFormat(new Date())],
  onOpen: (selectedDates, dateStr, instance) => {
    instance.set('maxDate', new Date())
  },

  onChange: (selectedDates, dateStr, instance) => {
    if (selectedDates.length === 1) {
      const maxAllowed = addDays(selectedDates[0], 90)

      instance.set('maxDate', maxAllowed > new Date() ? new Date() : maxAllowed)
    }

    if (selectedDates.length === 2) {
      instance.set('maxDate', new Date())
    }

    fromDate.value = customDateFormat(selectedDates[0])
    toDate.value = selectedDates[1]
      ? customDateFormat(selectedDates[1])
      : customDateFormat(selectedDates[0])

    dateRange.value = dateStr
  },
})

watch( dateRange, value => {
  if (value) {
    quickDateRange.value = quickDateRangeOptions.find(option => option.value === value)?.title || 'Custom'
  }
}, { immediate: true })

const itemsPerPage = ref(50)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()
const selectedCategory = ref(null)
const selectedDepartment = ref(null)


const { data: departments, isFetching: isFetchingDepartment } = await useApi(
  createUrl("masters/departments", {
    query: {
      select: ["id", "name"],
    },
  }),
)

const {
  data: categoriesData,
} = useApi(createUrl('/masters/categories', {
  query: computed(() => {
    const baseQuery = {
      sort: sortBy.value,
      order_by: orderBy.value,
      search: {
        department_id: selectedDepartment.value,
      },
    }

    if (itemsPerPage.value !== -1) {
      return {
        ...baseQuery,
        paged: 1,
        itemsPerPage: itemsPerPage.value,
        page: page.value,
      }
    }
    
    return baseQuery
  }),
}))

const categories = computed(() => {
  // When fetching all data
  if (itemsPerPage.value === -1) {
    return categoriesData.value || []
  }

  // Normal paginated response
  return categoriesData.value?.data || []
})

const ticketTypes = await getEnumValue('TicketTypes', true)

const { data: locationTypes } = await useApi(createUrl('/masters/location-types', {
  query: {
    search: {
      unit_id: unit_id.value,
    },
    select: ['id', 'name'],
  },
}))

const priorityOptions =  await getEnumValue('Priorities')


const ticketTableFilter = ref(null)
const isTicketTableDialogVisible = ref(false)
const pageType = ref('ticket-management')

const onUpdateTicketTableFilter = data => {

  if(data.page == "capa-management"){
    pageType.value = "capa-management"
  }else{
    pageType.value = "ticket-management"
  }
  const { page, ...restData } = data

  ticketTableFilter.value = {
    ...restData,
  }
  isTicketTableDialogVisible.value = true
}

watch(
  () => isTicketTableDialogVisible.value,
  value => {
    if (value === false) ticketTableFilter.value = null
  },
)

const tab = ref('rca')

const tabs = [
  { label: 'Executive Summary', value: 'RCA', component: ExecutiveSummary },
  { label: 'RCA & CA Analysis', value: 'RCACA', component: RcaCaAnalysis },
  { label: 'CAPA Management', value: 'CAPA', component: CapaManagement },
  { label: 'TAT & SLA Performance', value: 'TATSLA', component: TatSlaPerformance },
  { label: 'Category Deep-Dive', value: 'CategoryDeepDive', component: CategoryDeepDive },
  { label: 'Location Analytics', value: 'LocationAnalytics', component: LocationAnalytics },
  { label: 'Operations & Staff', value: 'OperationsStaff', component: OperationsStaff },
]
</script>

<template>
  <div>
    <VRow>
      <VCol cols="12">
        <span class="text-h4">
          Ticket Dashboard
        </span>
      </VCol>
    </VRow>
    <VRow>
      <VCol
        md="12"
        sm="12"
        xs="12"
      >
        <VCard
          variant="flat"
          class="border"
        >
          <VCardText>
            <!--
              <span class="text-h6 font-weight-bold text-primary text-capitalize">
              Filter Analytics By Date
              </span> 
            -->
            <VRow align="center">
              <VCol
                cols="12"
                sm="6"
                md="4"
              >
                <AppDateTimePicker
                  v-model="dateRange"
                  label="Range"
                  placeholder="Select date"
                  :config="dateRangeOptions"
                />
              </VCol>
            
              <VCol
                cols="12"
                sm="6"
                md="4"
              >
                <AppSelect
                  v-model="selectedDepartment"
                  label="Department"
                  :loading="isFetchingDepartment"
                  :items="departments"
                  :item-title="(item) => item?.name"
                  :item-value="(item) => item?.id"
                  clearable
                  placeholder="Filter By Departments"
                />
              </VCol>

              <VCol
                cols="12"
                sm="6"
                md="4"
              >
                <AppSelect
                  v-model="selectedCategory"
                  label="Category"
                  :items="categories"
                  :item-title="(item) => item?.name"
                  :item-value="(item) => item?.id"
                  clearable
                  placeholder="Filter By Parent Category"
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VRow>
      <VCol cols="12">
        <VCard
          variant="flat"
          class="border pa-0"
          elevation="0"
        >
          <VTabs v-model="tab">
            <VTab
              v-for="t in tabs"
              :key="t.value"
              :value="t.value"
              hide-slider
              class="no-border-tabs"
            >
              {{ t.label }}
            </VTab>
          </VTabs>
        
          <VCardText>
            <VWindow v-model="tab">
              <VWindowItem
                v-for="t in tabs"
                :key="t.value"
                :value="t.value"
              >
                <component
                  :is="t.component"
                  :key="unit_id+fromDate+toDate"
                  :from="fromDate"
                  :to="toDate"
                  :unit-id="unit_id"
                  :department-id="selectedDepartment"
                  :category-id="selectedCategory"
                  :ticket-types="ticketTypes"
                  :priority-options="priorityOptions"
                  @update:ticket-table-filter="onUpdateTicketTableFilter"
                />
              </VWindowItem>
            </VWindow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>

  <!-- Table Dialog -->
  <VDialog v-model="isTicketTableDialogVisible">
    <DialogCloseBtn
      size="small"
      @click="() => (isTicketTableDialogVisible = !isTicketTableDialogVisible)"
    />

    <VCard>
      <VCardText>
        <TicketTable
          ref="refTicketTable"
          :page="pageType"
          :ticket-filters="ticketTableFilter"
          :action-visible="false"
          :style="{
            cardVariant: 'flat',
          }"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>

